import React, { useEffect, useState } from "react";
import { Box, Button, Checkbox, Dialog, DialogActions, DialogContent, DialogTitle, FormControl, IconButton, InputLabel, ListItemText, MenuItem, Paper, Select, Table, TableBody, TableCell, TableHead, TablePagination, TableRow, TextField, Typography } from "@mui/material";
import jwtInterceptor from "../service/jwtInterceptor";
import { API_URL } from "../utils/constants";
import { ACL } from "../service/Enums";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import { AppCustomConfirmationDialog } from "../component/AppCustomConfirmationDialog";
import { useToast } from "../Context/ToastContext";
import RoleFilter from "../component/RoleFilter";

const PRIMARY_COLOR = "#3F50B6";
const SECONDARY_COLOR = "#000000";

const ACL_OPTIONS = [
    {value: ACL.TERRAINS_LECTURE, label: "Lecture des terrains"},
    {value: ACL.TERRAINS_ECRITURE, label: "Modification des terrains"},
    {value: ACL.CONTACTS_LECTURE, label: "Lecture des contacts"},
    {value: ACL.CONTACTS_ECRITURE, label: "Modification des contacts"},
    {value: ACL.UTILISATEURS_LECTURE, label: "Lecture des utilisateurs"},
    {value: ACL.UTILISATEURS_ECRITURE, label: "Modification des utilisateurs"},
    {value: ACL.ROLES_LECTURE, label: "Lecture des rôles"},
    {value: ACL.ROLES_ECRITURE, label: "Modification des rôles"},
];

const SELECT_MENU_PROPS = {
    sx: { zIndex: 1600 },
    PaperProps: { sx: { zIndex: 1600 } },
};

export default function RoleManagementPage() {
    const toaster = useToast();
    const [roles, setRoles] = useState([]);
    const [name, setName] = useState("");
    const [acls, setAcls] = useState([]);
    const [error, setError] = useState("");
    const [isCreateOpen, setIsCreateOpen] = useState(false);
    const [selectedRole, setSelectedRole] = useState(null);
    const [roleToDelete, setRoleToDelete] = useState(null);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [roleSearch, setRoleSearch] = useState("");

    const loadRoles = async () => {
        const response = await jwtInterceptor.get(`${API_URL}/roles`);
        setRoles(response.data?.data?.details || response.data?.data || []);
    };

    useEffect(() => {
        loadRoles().catch(() => setError("Impossible de charger les rôles."));
    }, []);

    useEffect(() => {
        const lastPage = Math.max(0, Math.ceil(roles.length / rowsPerPage) - 1);
        if (page > lastPage) setPage(lastPage);
    }, [roles.length, rowsPerPage, page]);

    const filteredRoles = roles.filter((role) =>
        role.name?.toLowerCase().includes(roleSearch.toLowerCase())
    );
    const paginatedRoles = filteredRoles.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

    const handleRoleSearch = (value) => {
        setRoleSearch(value);
        setPage(0);
    };

    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const toggleAcl = (acl) => {
        setAcls((current) => current.includes(acl)
            ? current.filter((item) => item !== acl)
            : [...current, acl]
        );
    };

    const createRole = async (event) => {
        event.preventDefault();
        setError("");
        try {
            const roleId = selectedRole?._id;
            await jwtInterceptor[roleId ? "put" : "post"](
                roleId ? `${API_URL}/roles/${roleId}` : `${API_URL}/roles`,
                { name, acls }
            );
            setName("");
            setAcls([]);
            setIsCreateOpen(false);
            setSelectedRole(null);
            await loadRoles();
            toaster.notifySuccess(roleId ? "Rôle modifié avec succès." : "Rôle créé avec succès.");
        } catch (requestError) {
            setError(requestError.response?.data?.data?.details?.[0] || "Impossible de créer le rôle.");
        }
    };

    const openCreate = () => {
        setSelectedRole(null);
        setName("");
        setAcls([]);
        setError("");
        setIsCreateOpen(true);
    };

    const openEdit = (role) => {
        setSelectedRole(role);
        setName(role.name || "");
        setAcls(role.acls || []);
        setError("");
        setIsCreateOpen(true);
    };

    const openDelete = (role) => {
        setRoleToDelete(role);
    };

    const closeDelete = () => {
        setRoleToDelete(null);
    };

    const deleteRole = async () => {
        if (!roleToDelete) return;
        try {
            await jwtInterceptor.delete(`${API_URL}/roles/${roleToDelete._id}`);
            closeDelete();
            await loadRoles();
            toaster.notifySuccess("Rôle supprimé avec succès.");
        } catch (requestError) {
            setError("Impossible de supprimer le rôle.");
        }
    };

    return (
        <Box sx={{ p: 3 }}>
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3 }}>
                <Typography variant="h4">Gestion des rôles</Typography>
                <Button
                    variant="contained"
                    onClick={openCreate}
                    sx={{ backgroundColor: PRIMARY_COLOR, "&:hover": { backgroundColor: "#222222" } }}
                >
                    Ajouter un Rôle
                </Button>
            </Box>
            <Paper sx={{ p: 2 }}>
                <Box sx={{ mb: 2, maxWidth: 360 }}>
                    <RoleFilter value={roleSearch} onChange={handleRoleSearch}/>
                </Box>
                <Table>
                    <TableHead>
                        <TableRow>
                            <TableCell>Nom du rôle</TableCell>
                            <TableCell>ACLs</TableCell>
                            <TableCell>Actions</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {paginatedRoles.map((role) => (
                            <TableRow key={role._id}>
                                <TableCell>{role.name}</TableCell>
                                <TableCell>{(role.acls || []).map((acl) => ACL_OPTIONS.find((option) => option.value === acl)?.label || acl).join(", ") || "Aucune"}</TableCell>
                                <TableCell sx={{ whiteSpace: "nowrap", p: 1 }}>
                                    <IconButton aria-label="Modifier" size="small" onClick={() => openEdit(role)}><EditIcon /></IconButton>
                                    <IconButton aria-label="Supprimer" color="error" size="small" onClick={() => openDelete(role)}><DeleteIcon /></IconButton>
                                </TableCell>
                            </TableRow>
                        ))}
                        {filteredRoles.length === 0 && (
                            <TableRow>
                                <TableCell colSpan={3} align="center">Aucun rôle trouvé</TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
                <TablePagination
                    component="div"
                    count={filteredRoles.length}
                    page={page}
                    onPageChange={handleChangePage}
                    rowsPerPage={rowsPerPage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                    labelRowsPerPage="Lignes par page"
                    backIconButtonProps={{ "aria-label": "Précédent" }}
                    nextIconButtonProps={{ "aria-label": "Suivant" }}
                />
            </Paper>
            <Dialog open={isCreateOpen} onClose={() => setIsCreateOpen(false)} fullWidth maxWidth="sm">
                <DialogTitle>{selectedRole ? "Modifier le rôle" : "Ajouter un Rôle"}</DialogTitle>
                <Box component="form" onSubmit={createRole}>
                    <DialogContent sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                        <TextField
                            label="Nom du rôle"
                            value={name}
                            onChange={(event) => setName(event.target.value)}
                            required
                            fullWidth
                        />
                        <FormControl fullWidth>
                            <InputLabel id="role-acls-label">ACLs</InputLabel>
                            <Select
                                labelId="role-acls-label"
                                multiple
                                value={acls}
                                label="ACLs"
                                onChange={(event) => setAcls(event.target.value)}
                                renderValue={(selected) => selected.map((acl) => ACL_OPTIONS.find((option) => option.value === acl)?.label || acl).join(", ")}
                                MenuProps={SELECT_MENU_PROPS}
                            >
                                {ACL_OPTIONS.map((acl) => (
                                    <MenuItem key={acl.value} value={acl.value}>
                                        <Checkbox checked={acls.includes(acl.value)} />
                                        <ListItemText primary={acl.label} />
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                        {error && <Typography color="error">{error}</Typography>}
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={() => setIsCreateOpen(false)} sx={{ backgroundColor: SECONDARY_COLOR, "&:hover": { backgroundColor: "#222222" } , color: "#ffffff"}}>
                            Annuler
                        </Button>
                        <Button
                            type="submit"
                            variant="contained"
                            sx={{ backgroundColor: PRIMARY_COLOR, "&:hover": { backgroundColor: "#222222" } }}
                        >
                            {selectedRole ? "Modifier le rôle" : "Créer le rôle"}
                        </Button>
                    </DialogActions>
                </Box>
            </Dialog>
            <AppCustomConfirmationDialog
                open={Boolean(roleToDelete)}
                onClose={closeDelete}
                onAccept={deleteRole}
                onDecline={closeDelete}
                title="Confirmation de suppression"
                bodyMessage={`Êtes-vous sûr de vouloir supprimer le rôle « ${roleToDelete?.name || ""} » ? Cette action est irréversible.`}
                acceptButtonText="Supprimer"
                declineButtonText="Annuler"
                acceptButtonColor="error"
            />
        </Box>
    );
}
